import { useCallback, useState, useMemo, useEffect } from "react";
import { Tag, TimeSlot } from "@clockwise/schema";
import { FormattedSlot } from "./CopyTimesPreview";
import { DateTime } from "luxon";
import { getDaySuffix } from "@util/date.util";
import { useQuery } from "@apollo/client";
import { bestTimesQuery } from "./copy-best-times-modal.graphql";
import {
  BestTimes,
  BestTimesVariables,
  BestTimes_viewer_schedulingLinkDetails_SchedulingLinkDetails_suggestedSlotsByDate_suggestedSlots as SuggestedSlot,
  BestTimes_viewer_schedulingLinkDetails_SchedulingLinkDetails_suggestedSlotsByDate as SuggestedSlotsByDate,
} from "./__generated__/BestTimes";
import { compact } from "lodash";
import { getError, getValue } from "@util/errorable.util";

export function suggestSlotToFormattedSlot(
  slot: SuggestedSlot,
  targetTimeZone: string,
): FormattedSlot {
  const startTime = DateTime.fromMillis(slot.timeRange.startTime.millis, {
    zone: targetTimeZone,
  });

  const now = DateTime.now().setZone(targetTimeZone);
  const isToday = startTime.hasSame(now, "day");
  const isTomorrow = startTime.hasSame(now.plus({ days: 1 }), "day");

  let relativeDateString = "";
  if (isToday) {
    relativeDateString = "Today";
  } else if (isTomorrow) {
    relativeDateString = "Tomorrow";
  } else {
    relativeDateString = startTime.toFormat("ccc");
  }
  relativeDateString += startTime.toFormat(", LLL d") + getDaySuffix(startTime.day);

  return {
    startTime12h: startTime.toFormat("h:mma").toLowerCase(),
    startTime24h: startTime.toFormat("HH:mm"),
    isoDate: startTime.toISODate(),
    relativeDateString: relativeDateString,
    startMillis: slot.timeRange.startTime.millis,
    timeZoneName: startTime.offsetNameShort,
  };
}

export function usePaginatedBestTimes(
  linkName: string,
  slug: string,
  timeZone: string,
  numDaysToShow: number,
  daysToFetchAtOneTime = 7,
) {
  const [fetchedRange, setFetchedRange] = useState<[DateTime, DateTime]>([
    DateTime.now(),
    DateTime.now().plus({ days: 7 }),
  ]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { loading, error, fetchMore, data } = useQuery<BestTimes, BestTimesVariables>(
    bestTimesQuery,
    {
      fetchPolicy: "cache-and-network", // quick results from cache, true data back from network.
      //skip: true, // Skips the initial fetch upon page load. Allows to use just fetchMore.
      notifyOnNetworkStatusChange: true, // This is needed to make the loading variable work according to Apollo docs.
      variables: {
        linkName,
        slug,
        body: {
          timeZone: timeZone,
          dayTimeSlotRange: {
            minSlot: {
              date: fetchedRange[0].toISODate(),
              timeSlot: TimeSlot.T_00_00,
            },
            maxSlot: {
              date: fetchedRange[1].toISODate(),
              timeSlot: TimeSlot.T_23_30,
            },
          },
        },
      },
      onError: () => {
        setErrorMsg("Sorry, something went wrong loading available times");
      },
    },
  );

  const linkData = getValue(data?.viewer.schedulingLinkDetails);
  const publicMeetingSettings = getValue(linkData?.publicMeetingSettings);

  const generateSuggestedTimes = useCallback(
    (suggestedSlotsByDate: SuggestedSlotsByDate[]) => {
      const newBestTimes = suggestedSlotsByDate
        .map((slotsByDate) => {
          // Suggestions per day is contructed from:
          // "best" time, if present.
          // 1 non best time, arbitrarily choosen to be the middle time of the array until we have a backend based rank of best times.
          const nonBestTimes = slotsByDate.suggestedSlots.filter((slot) => slot.tag !== Tag.Best);
          const nonInconvenientTimes = slotsByDate.suggestedSlots.filter(
            (slot) => slot.tag !== Tag.Best && slot.tag !== Tag.Inconvenient,
          );
          const toGrabFrom = nonInconvenientTimes.length ? nonInconvenientTimes : nonBestTimes;
          return compact([
            slotsByDate.suggestedSlots.find((slot) => slot.tag === Tag.Best), // best time
            toGrabFrom?.[Math.floor(toGrabFrom.length / 2)], // Middle time excluding best time, non-inconvenient if possible.
          ]);
        })
        .filter((suggestedSlots) => suggestedSlots.length)
        .slice(0, numDaysToShow);

      const numDays = newBestTimes.length;

      return [numDays, newBestTimes.flat()] as const;
    },
    [numDaysToShow],
  );

  const fetchMoreIfPossible = useCallback(() => {
    const lastFetchedDate = fetchedRange[1];

    if (
      !publicMeetingSettings ||
      publicMeetingSettings.endOfAvailability.millis < lastFetchedDate.toMillis()
    ) {
      return;
    }

    const newFetchedDates: [DateTime, DateTime] = [
      fetchedRange[0].plus({ days: daysToFetchAtOneTime }),
      fetchedRange[1].plus({ days: daysToFetchAtOneTime }),
    ];

    setFetchedRange(newFetchedDates);

    // the result is handled in the same `onCompleted` function above
    void fetchMore({
      variables: {
        linkName,
        slug,
        body: {
          timeZone: timeZone,
          dayTimeSlotRange: {
            minSlot: {
              date: newFetchedDates[0].toISODate(),
              timeSlot: TimeSlot.T_00_00,
            },
            maxSlot: {
              date: newFetchedDates[1].toISODate(),
              timeSlot: TimeSlot.T_23_30,
            },
          },
        },
      },
    });
  }, [
    daysToFetchAtOneTime,
    fetchMore,
    fetchedRange,
    linkName,
    publicMeetingSettings,
    slug,
    timeZone,
  ]);

  useEffect(() => {
    const linkData = getValue(data?.viewer.schedulingLinkDetails);
    if ((!loading && !linkData) || getError(data?.viewer.schedulingLinkDetails)) {
      setErrorMsg("Sorry, something went wrong loading available times");
    } else {
      setErrorMsg("");
    }
  }, [data?.viewer.schedulingLinkDetails, loading, numDaysToShow]);

  const bestTimeSlots = useMemo(() => {
    const linkData = getValue(data?.viewer.schedulingLinkDetails);
    if (linkData && !getError(data?.viewer.schedulingLinkDetails)) {
      const [numDays, bestTimes] = generateSuggestedTimes(linkData.suggestedSlotsByDate);
      if (numDays < numDaysToShow) {
        fetchMoreIfPossible();
      }
      return bestTimes;
    } else {
      return [];
    }
  }, [
    data?.viewer.schedulingLinkDetails,
    fetchMoreIfPossible,
    generateSuggestedTimes,
    numDaysToShow,
  ]);

  return {
    loading,
    error,
    publicMeetingSettings,
    bestTimeSlots,
    errorMsg,
  };
}
